"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import API from "../../services/api"

function LoginPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [emailFocused, setEmailFocused] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [lang, setLang] = useState("fr")
  const [mounted, setMounted] = useState(false)
  const navigate = useNavigate()

  const dict = {
    fr: {
      tag: "Espace Administration",
      title: "Bon retour",
      subtitle: "Connectez-vous à votre espace de gestion Ibis Tunis",
      emailLabel: "Adresse e-mail",
      emailPlaceholder: "admin@ibis-tunis.com",
      passwordLabel: "Mot de passe",
      passwordPlaceholder: "••••••••",
      remember: "Se souvenir de moi",
      forgot: "Mot de passe oublié ?",
      login: "Se connecter",
      loading: "Connexion...",
      security: "Accès sécurisé · Réservé au personnel autorisé",
      home: "Accueil",
    },
    en: {
      tag: "Admin Space",
      title: "Welcome back",
      subtitle: "Sign in to your Ibis Tunis management space",
      emailLabel: "Email address",
      emailPlaceholder: "admin@ibis-tunis.com",
      passwordLabel: "Password",
      passwordPlaceholder: "••••••••",
      remember: "Remember me",
      forgot: "Forgot password?",
      login: "Sign in",
      loading: "Signing in...",
      security: "Secure access · Authorized staff only",
      home: "Home",
    },
    ar: {
      tag: "فضاء الإدارة",
      title: "مرحباً بعودتك",
      subtitle: "سجّل دخولك إلى فضاء إدارة إيبيس تونس",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "admin@ibis-tunis.com",
      passwordLabel: "كلمة المرور",
      passwordPlaceholder: "••••••••",
      remember: "تذكرني",
      forgot: "نسيت كلمة المرور؟",
      login: "تسجيل الدخول",
      loading: "جاري الدخول...",
      security: "وصول آمن · للموظفين المخوّلين فقط",
      home: "الرئيسية",
    },
  }

  const t = dict[lang] || dict.fr
  const isRTL = lang === "ar"

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail")
    if (savedEmail) { setEmail(savedEmail); setRememberMe(true) }
    setTimeout(() => setMounted(true), 60)
  }, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    setError("")
    if (!email || !password) { setError("Veuillez remplir tous les champs."); return }
    setLoading(true)
    try {
      const response = await API.post("/auth/login", { email, password })
      localStorage.setItem("accessToken", response.data.accessToken)
      localStorage.setItem("user", JSON.stringify(response.data.user))
      if (rememberMe) localStorage.setItem("rememberedEmail", email)
      else localStorage.removeItem("rememberedEmail")
      navigate("/dashboard")
    } catch (err) {
      setError(err.response?.data?.message || "Identifiants incorrects. Veuillez réessayer.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .lp-root {
          min-height: 100vh;
          display: flex;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          background: #0a0a0f;
          position: relative;
          overflow: hidden;
        }

        /* ── Animated background ── */
        .lp-bg {
          position: absolute;
          inset: 0;
          background-image: url('/images/ibis-background.jpg');
          background-size: cover;
          background-position: center;
          filter: brightness(0.35) saturate(0.8);
          z-index: 0;
        }
        .lp-bg-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg,
            rgba(10,10,20,0.72) 0%,
            rgba(180,10,40,0.18) 50%,
            rgba(10,10,20,0.85) 100%);
          z-index: 1;
        }
        .lp-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.25;
          pointer-events: none;
          z-index: 1;
        }
        .lp-orb-1 {
          width: 500px; height: 500px;
          background: radial-gradient(circle, #e11d48, transparent 70%);
          top: -120px; right: -120px;
          animation: lp-drift 18s ease-in-out infinite;
        }
        .lp-orb-2 {
          width: 360px; height: 360px;
          background: radial-gradient(circle, #7c3aed, transparent 70%);
          bottom: -80px; left: -80px;
          animation: lp-drift 22s ease-in-out infinite reverse;
        }
        @keyframes lp-drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(30px, -30px); }
        }

        /* ── Left branding panel ── */
        .lp-left {
          flex: 1.1;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 56px 60px;
          position: relative;
          z-index: 2;
        }
        .lp-brand-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(225,29,72,0.15);
          border: 1px solid rgba(225,29,72,0.35);
          border-radius: 999px;
          padding: 6px 14px;
          color: #f87171;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 28px;
          width: fit-content;
          backdrop-filter: blur(8px);
        }
        .lp-brand-pill-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #e11d48;
          animation: lp-pulse 2s ease-in-out infinite;
        }
        @keyframes lp-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.7); }
        }
        .lp-brand-logo {
          height: 44px;
          width: auto;
          margin-bottom: 32px;
          filter: brightness(0) invert(1);
          opacity: 0.95;
        }
        .lp-brand-headline {
          font-size: clamp(32px, 4vw, 52px);
          font-weight: 800;
          color: #ffffff;
          line-height: 1.1;
          letter-spacing: -1.5px;
          margin-bottom: 18px;
        }
        .lp-brand-headline em {
          font-style: normal;
          color: #f43f5e;
        }
        .lp-brand-desc {
          font-size: 15px;
          color: rgba(255,255,255,0.55);
          line-height: 1.7;
          max-width: 380px;
          margin-bottom: 48px;
          font-weight: 400;
        }
        .lp-brand-stats {
          display: flex;
          gap: 36px;
          padding-top: 32px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .lp-stat-value {
          font-size: 26px;
          font-weight: 800;
          color: #fff;
          letter-spacing: -0.5px;
        }
        .lp-stat-label {
          font-size: 11px;
          color: rgba(255,255,255,0.4);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-top: 2px;
          font-weight: 500;
        }

        /* ── Right form panel ── */
        .lp-right {
          flex: 0 0 480px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 32px 40px;
          position: relative;
          z-index: 2;
          background: rgba(10,10,18,0.65);
          backdrop-filter: blur(24px);
          border-left: 1px solid rgba(255,255,255,0.07);
        }

        /* Top bar */
        .lp-topbar {
          position: absolute;
          top: 20px;
          left: 24px;
          right: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .lp-lang-group {
          display: flex;
          gap: 4px;
        }
        .lp-lang-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 10px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.05);
          color: rgba(255,255,255,0.5);
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          letter-spacing: 0.04em;
        }
        .lp-lang-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .lp-lang-btn.active {
          background: rgba(225,29,72,0.2);
          border-color: rgba(225,29,72,0.5);
          color: #f87171;
        }
        .lp-lang-flag {
          width: 14px; height: 14px;
          border-radius: 50%;
          object-fit: cover;
        }
        .lp-home-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.06);
          color: rgba(255,255,255,0.65);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
        }
        .lp-home-btn:hover {
          background: rgba(255,255,255,0.12);
          color: #fff;
          border-color: rgba(255,255,255,0.25);
        }

        /* Card */
        .lp-card {
          width: 100%;
          max-width: 400px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }
        .lp-card.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Card header */
        .lp-card-header {
          margin-bottom: 36px;
        }
        .lp-card-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #f43f5e;
          margin-bottom: 14px;
        }
        .lp-card-tag::before {
          content: '';
          display: inline-block;
          width: 18px;
          height: 2px;
          background: #e11d48;
          border-radius: 2px;
        }
        .lp-card-title {
          font-size: 32px;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.8px;
          line-height: 1.15;
          margin-bottom: 10px;
        }
        .lp-card-subtitle {
          font-size: 14px;
          color: rgba(255,255,255,0.4);
          line-height: 1.6;
          font-weight: 400;
        }

        /* Error */
        .lp-error {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(239,68,68,0.12);
          border: 1px solid rgba(239,68,68,0.3);
          border-radius: 10px;
          margin-bottom: 20px;
          animation: lp-shake 0.35s ease;
        }
        @keyframes lp-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        .lp-error-icon {
          flex-shrink: 0;
          width: 18px; height: 18px;
          color: #f87171;
        }
        .lp-error-text {
          font-size: 13px;
          color: #fca5a5;
          font-weight: 500;
        }

        /* Input group */
        .lp-field {
          margin-bottom: 18px;
        }
        .lp-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: rgba(255,255,255,0.5);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 8px;
        }
        .lp-input-wrap {
          position: relative;
          display: flex;
          align-items: center;
          border-radius: 12px;
          border: 1.5px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.05);
          transition: all 0.25s ease;
        }
        .lp-input-wrap.focused {
          border-color: #e11d48;
          background: rgba(225,29,72,0.06);
          box-shadow: 0 0 0 4px rgba(225,29,72,0.1);
        }
        .lp-input-icon {
          position: absolute;
          left: 14px;
          color: rgba(255,255,255,0.25);
          pointer-events: none;
          transition: color 0.2s;
          flex-shrink: 0;
        }
        .lp-input-wrap.focused .lp-input-icon { color: #e11d48; }
        .lp-input {
          flex: 1;
          padding: 14px 14px 14px 44px;
          font-size: 15px;
          background: transparent;
          border: none;
          outline: none;
          color: #ffffff;
          font-weight: 400;
          width: 100%;
        }
        .lp-input::placeholder { color: rgba(255,255,255,0.2); }
        .lp-eye-btn {
          background: none;
          border: none;
          padding: 0 14px;
          cursor: pointer;
          color: rgba(255,255,255,0.25);
          display: flex;
          align-items: center;
          transition: color 0.2s;
          flex-shrink: 0;
        }
        .lp-eye-btn:hover { color: rgba(255,255,255,0.7); }

        /* Actions row */
        .lp-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 28px;
        }
        .lp-remember {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        .lp-remember input[type="checkbox"] {
          width: 16px; height: 16px;
          accentColor: #e11d48;
          cursor: pointer;
          border-radius: 4px;
        }
        .lp-remember-text {
          font-size: 13px;
          color: rgba(255,255,255,0.45);
          font-weight: 500;
          user-select: none;
        }
        .lp-forgot {
          background: none;
          border: none;
          font-size: 13px;
          color: #f43f5e;
          cursor: pointer;
          font-weight: 600;
          padding: 0;
          transition: opacity 0.2s;
        }
        .lp-forgot:hover { opacity: 0.75; }

        /* Submit button */
        .lp-submit {
          width: 100%;
          padding: 15px 24px;
          font-size: 15px;
          font-weight: 700;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          background: linear-gradient(135deg, #e11d48 0%, #9f1239 100%);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          letter-spacing: 0.2px;
          transition: all 0.25s ease;
          box-shadow: 0 8px 28px rgba(225,29,72,0.4);
          position: relative;
          overflow: hidden;
          margin-bottom: 28px;
        }
        .lp-submit::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.15), transparent);
          opacity: 0;
          transition: opacity 0.2s;
        }
        .lp-submit:hover:not(:disabled)::before { opacity: 1; }
        .lp-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 36px rgba(225,29,72,0.5);
        }
        .lp-submit:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 6px 18px rgba(225,29,72,0.35);
        }
        .lp-submit:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        .lp-spinner {
          width: 18px; height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: lp-spin 0.7s linear infinite;
          flex-shrink: 0;
        }
        @keyframes lp-spin { to { transform: rotate(360deg); } }

        /* Footer */
        .lp-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 20px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }
        .lp-security {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: rgba(255,255,255,0.25);
          font-weight: 500;
        }
        .lp-security-icon { color: rgba(255,255,255,0.2); flex-shrink: 0; }
        .lp-powered {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .lp-powered-logo {
          height: 14px;
          width: auto;
          opacity: 0.35;
          filter: brightness(0) invert(1);
        }

        /* RTL */
        .lp-root[dir="rtl"] .lp-topbar { flex-direction: row-reverse; }
        .lp-root[dir="rtl"] .lp-input { padding: 14px 44px 14px 14px; }
        .lp-root[dir="rtl"] .lp-input-icon { left: auto; right: 14px; }
        .lp-root[dir="rtl"] .lp-eye-btn { order: -1; }
        .lp-root[dir="rtl"] .lp-card-tag::before { order: 1; }

        /* Responsive */
        @media (max-width: 900px) {
          .lp-left { display: none; }
          .lp-right {
            flex: 1;
            border-left: none;
            background: rgba(10,10,18,0.8);
          }
        }
        @media (max-width: 480px) {
          .lp-right { padding: 24px 20px; }
          .lp-card-title { font-size: 26px; }
        }
      `}</style>

      <div className="lp-root" dir={isRTL ? "rtl" : "ltr"}>
        {/* Background */}
        <div className="lp-bg" />
        <div className="lp-bg-overlay" />
        <div className="lp-orb lp-orb-1" />
        <div className="lp-orb lp-orb-2" />

        {/* ── Left branding ── */}
        <div className="lp-left">
          <div className="lp-brand-pill">
            <span className="lp-brand-pill-dot" />
            Ibis Tunis
          </div>
          <img src="/images/logo2.webp" alt="Ibis" className="lp-brand-logo" />
          <h1 className="lp-brand-headline">
            Gérez votre<br />hôtel avec <em>style</em>
          </h1>
          <p className="lp-brand-desc">
            Plateforme d'administration centralisée pour l'équipe Ibis Tunis — contenus, services et bien plus.
          </p>
          <div className="lp-brand-stats">
            {[
              { value: "24/7", label: "Disponible" },
              { value: "100%", label: "Sécurisé" },
              { value: "3", label: "Langues" },
            ].map(({ value, label }) => (
              <div key={label}>
                <div className="lp-stat-value">{value}</div>
                <div className="lp-stat-label">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="lp-right">
          {/* Top bar */}
          <div className="lp-topbar">
            <div className="lp-lang-group">
              {[
                { code: "fr", flag: "/images/fr-flag-v2.png", label: "FR" },
                { code: "en", flag: "/images/uk-flag-v2.png", label: "EN" },
                { code: "ar", flag: "/images/ar-flag-v2.png", label: "AR" },
              ].map(({ code, flag, label }) => (
                <button
                  key={code}
                  type="button"
                  className={`lp-lang-btn${lang === code ? " active" : ""}`}
                  onClick={() => setLang(code)}
                >
                  <img src={flag} alt={label} className="lp-lang-flag" />
                  {label}
                </button>
              ))}
            </div>
            <button type="button" className="lp-home-btn" onClick={() => navigate("/home")}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              {t.home}
            </button>
          </div>

          {/* Card */}
          <div className={`lp-card${mounted ? " visible" : ""}`}>
            {/* Header */}
            <div className="lp-card-header">
              <div className="lp-card-tag">{t.tag}</div>
              <h2 className="lp-card-title">{t.title}</h2>
              <p className="lp-card-subtitle">{t.subtitle}</p>
            </div>

            {/* Error */}
            {error && (
              <div className="lp-error">
                <svg className="lp-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span className="lp-error-text">{error}</span>
              </div>
            )}

            <form onSubmit={handleLogin}>
              {/* Email */}
              <div className="lp-field">
                <label className="lp-label" htmlFor="lp-email">{t.emailLabel}</label>
                <div className={`lp-input-wrap${emailFocused ? " focused" : ""}`}>
                  <svg className="lp-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <input
                    id="lp-email"
                    className="lp-input"
                    type="email"
                    placeholder={t.emailPlaceholder}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError("") }}
                    onFocus={() => setEmailFocused(true)}
                    onBlur={() => setEmailFocused(false)}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              {/* Password */}
              <div className="lp-field">
                <label className="lp-label" htmlFor="lp-password">{t.passwordLabel}</label>
                <div className={`lp-input-wrap${passwordFocused ? " focused" : ""}`}>
                  <svg className="lp-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <input
                    id="lp-password"
                    className="lp-input"
                    type={showPassword ? "text" : "password"}
                    placeholder={t.passwordPlaceholder}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError("") }}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="lp-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? "Masquer" : "Afficher"}
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Remember / Forgot */}
              <div className="lp-row">
                <label className="lp-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className="lp-remember-text">{t.remember}</span>
                </label>
                <button type="button" className="lp-forgot" onClick={() => alert("Fonctionnalité à venir")}>
                  {t.forgot}
                </button>
              </div>

              {/* Submit */}
              <button type="submit" className="lp-submit" disabled={loading}>
                {loading ? (
                  <><div className="lp-spinner" />{t.loading}</>
                ) : (
                  <>
                    {t.login}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="lp-footer">
              <div className="lp-security">
                <svg className="lp-security-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                {t.security}
              </div>
              <div className="lp-powered">
                <img
                  src="/images/logo_it_bafa.png"
                  alt="IT-BAFA"
                  className="lp-powered-logo"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default LoginPage
